<template>
  <div class="task-container">
    <v-card class="pa-4">
      <v-card-title style="padding-right: 0px;">Task Management <v-btn variant="text"  color="primary" class="mb-4" style="float: right" @click="syncTasks">Sync All Tasks</v-btn></v-card-title>
        <div class="task-layout">
          <!-- Task List on the left -->
          <div class="task-list">
            <v-list>
              <v-list-item
                  v-for="(task, index) in tasks"
                  :key="task.id"
                  @click="selectTask(index)"
                  :class="{ 'active-task': selectedTaskIndex === index }"
              >
                <v-list-item-title>
                  <span style="max-width: 210px;display: inline-block;text-overflow: ellipsis;overflow: hidden;margin-top: 4px;">{{ task.summary || `Task ${index + 1}` }}</span>
                  <v-chip
                    :color="getSyncChipColor(task)"
                    style="float: right"
                >
                  {{ getSyncChipText(task) }}
                </v-chip></v-list-item-title>
              </v-list-item>
            </v-list>
            <v-btn color="primary" variant="text"  class="mb-4 w-100" @click="addTask()">Add Task</v-btn>
          </div>

          <!-- Task Details on the right -->
          <div class="task-details">
            <div v-if="selectedTask !== null">
              <v-text-field
                  v-if="selectedTask.trackerKey"
                  v-model="selectedTask.trackerKey"
                  label="JiraKey"
                  outlined
                  class="mb-3"
                  readonly
                  hide-details="auto"
                  @click:append-inner="openOnTracker(selectedTask.trackerKey)"
                  append-inner-icon="mdi-open-in-new"
              ></v-text-field>

              <v-text-field
                  v-model="selectedTask.summary"
                  label="Task Summary"
                  outlined
                  class="mb-3"
                  hide-details="auto"
                  @input="setDirty('summary')"
              ></v-text-field>


              <v-select
                  v-model="selectedTask.qualityAspect"
                  label="Quality Aspect"
                  :items="qualityAspectsItems"
                  item-props
                  item-title="name"
                  item-value="value"
                  hide-details="auto"
                  class="mb-3"
                  @update:modelValue="setDirty('qualityAspect')">
                <template #item="data">
                  <v-list-subheader v-if="data.props.header">
                    {{ data.props.header }}
                  </v-list-subheader>
                  <v-divider v-else-if="data.props.divider" />
                  <v-list-item v-else v-bind="data.props">
                  </v-list-item>
                </template>
              </v-select>

              <v-select
                  v-model="selectedTask.assignee"
                  :items="assignees"
                  item-title="name"
                  item-value="id"
                  label="Select Assignee"
                  outlined
                  class="mb-3"
                  hide-details="auto"
                  @update:modelValue="setDirty('assignee')"
              ></v-select>

              <v-select
                  v-model="selectedTask.parentIssue"
                  :items="parentIssues"
                  item-value="key"
                  item-title="summary"
                  label="Select Parent Issue"
                  outlined
                  class="mb-3"
                  hide-details="auto"
                  @update:modelValue="setDirty('parentIssue')"
              ></v-select>

              <v-menu
                  v-model="dueDatePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
              >
                <template v-slot:activator="{ props }">
                  <v-text-field
                      v-bind="props"
                      label="Due Date"
                      v-model="selectedTask.dueDateString"
                      prepend-inner-icon="mdi-calendar"
                      variant="outlined"
                      outlined
                      hide-details="auto"
                      class="mb-3"
                      @update:modelValue="setDirty('dueDate')"
                  />
                </template>
                <v-date-picker
                    v-model="selectedTask.dueDate"
                    color="primary"
                    no-title
                    @update:modelValue="(value) => {
                    selectedTask.dueDate = value;
                    dueDatePicker = false;
                    selectedTask.dueDateString = formatDate(value);
                    setDirty('dueDate');
                  }"
                ></v-date-picker>
              </v-menu>

              <v-text-field
                  v-model="selectedTask.originalEffort"
                  label="Original Effort (e.g., 2w 1d 4h)"
                  outlined
                  class="mb-3"
                  :rules="[effortRule]"
                  hide-details="auto"
                  @input="setDirty('originalEffort')"
              ></v-text-field>

              <v-textarea
                  v-model="selectedTask.description"
                  label="Description"
                  outlined
                  rows="5"
                  class="mb-3"
                  hide-details="auto"
                  @input="setDirty('description')"
              ></v-textarea>
              <div style="text-align: center">
                <v-btn variant="text" color="success" @click="updateTask">Save Task</v-btn>
                <v-btn variant="text" color="success" @click="duplicateTask(selectedTask)" class="ml-2">Duplicate Task</v-btn>
                <v-btn variant="text" color="error" @click="removeTask(selectedTask)" class="ml-2">Remove Task</v-btn>
              </div>
            </div>
            <div v-else>
              <p>Please select a task from the list</p>
            </div>
          </div>
        </div>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Loading Indicator -->
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import {loadTasks, addTask, deleteTask, saveTask, updateIssue, checkConflickts} from "@/workshop/workshopFunctions";
import { JiraTracker } from "@/workshop/JiraTracker";
import { loadTaskConfig } from "@/workshop/customerFunctions";
import segmentData from "@/data/segment.json";

export default {
  name: "TaskView",
  data() {
    return {
      items: [
        // Add Optgroup option (Dark Themes)
        { header: "A" },
        { name: "Night Bright", themeFile: "night-bright.css" },
        { name: "Dracula", themeFile: "dracula.css" },
        // Add Optgroup option (Light Themes)
        { header: "B" },
        { name: "Day Bright", themeFile: "day-bright.css" },
        { name: "Blue", themeFile: "blue.css" }
      ],
      qualityAspects: segmentData,
      qualityAspectsItems: [],
      tasks: [],
      selectedTaskIndex: null,
      assignees: [],
      parentIssues: [],
      tracker: null,
      dueDatePicker: false,
      loading: true,
      snackbar: {
        show: false,
        color: "success",
        text: "",
      },
    };
  },
  computed: {
    customerId() {
      return this.$route.params.customerId;
    },
    workshopId() {
      return this.$route.params.id;
    },
    selectedTask() {
      return this.tasks[this.selectedTaskIndex] || null;
    },
  },
  methods: {
    effortRule(val) {
      if (!val) return true;
      const trimmed = String(val).trim();
      const valid = /^(\d+\s*[hdw]\s*)+$/i.test(trimmed);
      return valid || 'Use only h, d or w (e.g., 2w 1d 4h)';
    },
    openOnTracker(trackerKey){
      window.open(this.tracker.getIssueUrl(trackerKey), "_blank");
    },
    setDirty(field) {
      if (this.selectedTask) {
        this.selectedTask.dirty[field] = true;

        // Labels initialisieren, falls sie nicht existieren
        if (!Array.isArray(this.selectedTask.labels)) {
          this.selectedTask.labels = [];
        }

        // Qualität in Labels hinzufügen/entfernen
        if (field === "qualityAspect") {
          const currentLabelIndex = this.selectedTask.labels.findIndex((label) =>
              label.startsWith("QA_")
          );
          if (currentLabelIndex !== -1) {
            this.selectedTask.labels.splice(currentLabelIndex, 1);
          }
          this.selectedTask.labels.push(`QA_${this.selectedTask.qualityAspect}`);
        }
      }
    },
    showSnackbar(message, color = "success") {
      this.snackbar.text = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    async duplicateTask(task){
      await this.addTask(task);
    },
    async addTask(duplicatingTask = null) {
      let newtask;
      try {
        if(duplicatingTask == null){
          newtask = {
            summary: "",
            qualityAspect: "",
            assignee: "",
            parentIssue: "",
            dueDate: null,
            originalEffort: "",
            description: "",
            trackerKey: null,
            labels: [], // Labels als leeres Array initialisieren
            dirty: {
              summary: false,
              assignee: false,
              parentIssue: false,
              dueDate: false,
              originalEffort: false,
              description: false,
              qualityAspect: false,
            },
          };
        } else {
          newtask = {
            summary: duplicatingTask.summary + " (Copy)",
            qualityAspect: duplicatingTask.qualityAspect,
            assignee: duplicatingTask.assignee,
            parentIssue: duplicatingTask.parentIssue,
            dueDate: duplicatingTask.dueDate,
            dueDateString: duplicatingTask.dueDateString,
            originalEffort: duplicatingTask.originalEffort,
            description: duplicatingTask.description,
            trackerKey: duplicatingTask.trackerKey,
            labels: duplicatingTask.labels || [],
            dirty: {
              summary: false,
              assignee: false,
              parentIssue: false,
              dueDate: false,
              originalEffort: false,
              description: false,
              qualityAspect: false,
            },
          };
        }
        const taskId = await addTask(this.customerId, this.workshopId, newtask);
        newtask.id = taskId;
        this.tasks.push(newtask);
        this.selectedTaskIndex = this.tasks.length - 1;

        this.showSnackbar("Task added");
      } catch (error) {
        console.error("Error adding task", error);
        this.showSnackbar("Error adding task", "error");
      }
    },
    async updateTask() {
      if (this.selectedTask) {
        try {
          await saveTask(this.customerId, this.workshopId, {
            ...this.selectedTask
          });
          this.showSnackbar("Task updated");
        } catch (error) {
          console.error("Error updating task", error);
          this.showSnackbar("Error updating task", "error");
        }
      }
    },
    async removeTask(task) {
      const hasText = (task?.summary || '').trim().length > 0 || (task?.description || '').trim().length > 0;
      const ok = hasText
        ? window.confirm('Are you sure you want to delete this task? This action cannot be undone.')
        : window.confirm('Delete empty task?');
      if (!ok) return;
      try {
        await deleteTask(this.customerId, this.workshopId, task);
        this.tasks = this.tasks.filter((taskEntry) => taskEntry.id !== task.id);
        if (this.selectedTaskIndex !== null && this.tasks.length > 0) {
          this.selectedTaskIndex = Math.min(this.selectedTaskIndex, this.tasks.length - 1);
        } else {
          this.selectedTaskIndex = null;
        }
        this.showSnackbar("Task removed", "warning");
      } catch (error) {
        console.error("Error removing task", error);
        this.showSnackbar("Error removing task", "error");
      }
    },
    async loadTasks() {
      try {
        const tasks = await loadTasks(this.customerId, this.workshopId);
        this.tasks = tasks.map((task) => ({
          ...task,
          dueDateString: task.dueDate ? this.formatDate(task.dueDate) : null,
          issueTypes: [],
        }));
        if (this.tasks.length > 0) {
          this.selectTask(0);
        }
        this.showSnackbar("Tasks loaded successfully");
      } catch (error) {
        console.error("Error loading tasks", error);
        this.showSnackbar("Error loading tasks", "error");
      } finally {
        this.loading = false;
      }
    },
    async loadConfigAndTracker() {
      try {
        const config = await loadTaskConfig(this.customerId);
        if (config) {
          this.tracker = new JiraTracker(config);
          try {
            this.assignees = await this.tracker.getAssignees();
            this.parentIssues = await this.tracker.getParentIssues();
            this.showSnackbar("Tracker successfully connected");
          } catch (trackerError) {
            console.error("Error connecting to tracker", trackerError);
            this.showSnackbar("Error connecting to tracker", "error");
          }
        } else {
          this.showSnackbar("No tracker configuration found", "error");
        }
      } catch (configError) {
        console.error("Error loading configuration", configError);
        this.showSnackbar("Error loading configuration", "error");
      }
    },
    selectTask(index) {
      this.selectedTaskIndex = index;
    },
    conflictCheck(){
      for (const task of this.tasks) {
        checkConflickts(task, this.tracker);
      }
      return false;
    },
    syncTasks() {
      this.loading = true;
      try {
        for (const task of this.tasks) {
          if(Object.values(task.dirty).some(value => value)){
            console.log("Syncing task", task);
            updateIssue(this.customerId, this.workshopId, task, this.tracker);
          }
        }
        this.showSnackbar("All tasks synced successfully");
      } catch (error) {
        console.error("Error syncing tasks", error);
        this.showSnackbar("Error syncing tasks", "error");
      } finally {
        this.loading = false;
      }
    },
    buildDropDownAsects(){
      let items = [];
      for (const aspect of this.qualityAspects) {
        items.push({ header: aspect.text.en.name });
        for (const subAspect of aspect.children) {
          items.push({ name: subAspect.text.en.name, value: subAspect.text.en.name.replace(" ", "_")});
        }
      }
      return items;
    },
    getSyncChipColor(task) {
      if (!task.trackerKey || task.dirty && Object.values(task.dirty).some(value => value)) {
        return "red"; // Rot, wenn kein trackerKey oder der Task "dirty" ist
      }
      return "green"; // Grün, wenn trackerKey vorhanden und nichts "dirty" ist
    },
    getSyncChipText(task) {
      if (!task.trackerKey || task.dirty && Object.values(task.dirty).some(value => value)) {
        return "Need Sync"; // Text für nicht synchronisierte Tasks
      }
      return "Synced"; // Text für synchronisierte Tasks
    },
  },
  async mounted() {
    try {
      this.qualityAspectsItems = this.buildDropDownAsects();
      await this.loadConfigAndTracker();
      await this.loadTasks();
    } catch (error) {
      console.error("Error initializing task view", error);
      this.showSnackbar("Error initializing task view", "error");
    }
  },
};
</script>

<style>
/* CACHE BUST v2025-11-20 */
.task-container {
  max-width: 1200px;
  margin: 20px auto;
}
.task-layout {
  display: flex;
  gap: 0;
}
.task-list {
  width: 30% !important;
  min-width: 30% !important;
  max-width: 30% !important;
  flex: 0 0 30% !important;
  border-right: 1px solid #ccc;
  padding-right: 16px;
}
.task-details {
  width: 70% !important;
  min-width: 70% !important;
  max-width: 70% !important;
  flex: 0 0 70% !important;
  padding-left: 16px;
}
.active-task {
  background-color: #e0e0e0;
  border-radius: 5px;
}
</style>